import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import SendNoteIcon from '@assets/icons/send-note.svg';
import { Button } from '@/components/button';
import { EditOrderItemNoteParams } from '@/navigators/types';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { NoteContent } from '@/components/order-guide/edit-note-content-v2';
import { StyleSheet, View, ViewStyle } from 'react-native';
import { useCanWriteLineItemRequest } from '@/hooks/dynamic-config/use-can-write-line-item-request';
import { useDiscardChangesOnClose } from '@/hooks/use-discard-changes-on-close';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useSupplierDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useToasts } from '@/contexts/toast-context';

type Props = EditOrderItemNoteParams & {
  contentContainerStyle?: ViewStyle;
  onClose: () => void;
  onRegisterCloseHandler?: (handler: () => void) => void;
};

export const EditOrderItemNoteContent: React.FC<Props> = ({
  contentContainerStyle,
  initialNotes,
  onClose,
  onRegisterCloseHandler,
  onNotesSaved,
  variantPackUUID,
}) => {
  const { updateOrderItemNotes } = useNewOrderActionsContext();
  const { showErrorToast } = useToasts();
  const canWriteRequest = useCanWriteLineItemRequest();

  const noteMaxLength =
    useSupplierDynamicConfigValue('line_item_notes_config')?.max_length ?? 1000;

  const [requestValue, setRequestValue] = useState(initialNotes);
  const [isSaving, setIsSaving] = useState(false);

  const sectionCopy = {
    description: i18n.t('order_guide_note.request_tab_description'),
    label: i18n.t('order_guide_note.request_tab_label'),
    placeholder: i18n.t('order_guide_note.request_placeholder'),
  };

  const hasUnsavedChanges = useMemo(
    () => requestValue !== initialNotes,
    [initialNotes, requestValue],
  );

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await updateOrderItemNotes(requestValue || null, variantPackUUID, {
        onSuccess: onNotesSaved,
      });
      onClose();
    } catch (e) {
      showErrorToast();
    } finally {
      setIsSaving(false);
    }
  };

  const { discardConfirmationSheet } = useDiscardChangesOnClose({
    hasUnsavedChanges,
    isSaving,
    onClose,
    onRegisterCloseHandler,
    save: handleSave,
  });

  const handleRemove = () => {
    setRequestValue('');
  };

  return (
    <>
      <KeyboardAwareScrollView
        alwaysBounceVertical={false}
        bottomOffset={24}
        contentContainerStyle={[styles.scrollContent, contentContainerStyle]}
        keyboardDismissMode="on-drag"
      >
        <View style={styles.noteContentContainer}>
          <NoteContent
            description={sectionCopy.description}
            HeaderIcon={SendNoteIcon}
            label={sectionCopy.label}
            maxLength={noteMaxLength}
            onChangeText={setRequestValue}
            onRemove={handleRemove}
            placeholder={sectionCopy.placeholder}
            readOnly={!canWriteRequest}
            value={requestValue}
          />
        </View>
        {canWriteRequest ? (
          <Button
            loading={isSaving}
            onPress={handleSave}
            trackingData={{
              name: 'edit_order_item_note_modal.save_button',
            }}
          >
            {i18n.t('save')}
          </Button>
        ) : null}
      </KeyboardAwareScrollView>
      {discardConfirmationSheet}
    </>
  );
};

const styles = StyleSheet.create({
  noteContentContainer: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
});
